import { BoundingBox, TickEventManager } from '../util';
import { Engine } from '../Engine';
import { Context } from '../context/Context';
import { CompositeSceneRenderer, Decal, InstanceManager, RenderMode, SceneRenderer } from '../renderer';
import { vec3 } from 'gl-matrix';
import { LightManager } from '../light';
import { ObjectManager } from '../object';
import { TextureCache } from '../texture';
import { RenderObjectFactory } from '../object/RenderObjectFactory';
import { NavigationSystem, TiledNavMeshStreamer } from '../navigation-mesh';
import type { TiledBvh } from '../spatial/bvh/TiledBvh';
import { AgentRegistry } from '../spatial/agents/AgentRegistry';
import { RenderObject } from '../object/RenderObject';
import { InputHandler } from '../input';
import { PickingManager } from '../picking/PickingManager';
import { TextManager } from '../text';
import { OverlayManager } from '../overlay';
import { NavigationAwareObject } from '../object/NavigationAwareObject';
import { PhysicsFeature } from '../physics';
import { ParsedModel, SceneManifestLoader } from './SceneManifestLoader';
import { SceneManifest } from './SceneManifest';
import { TiledTerrainStreamer } from './TiledTerrainStreamer';
import { TiledObjectStreamer } from './TiledObjectStreamer';
import { ModelLodKit } from './loaders/ModelLodKit';
import { SceneLoadProgressTracker } from './SceneLoadProgress';
import { SceneFeature } from './feature';
import { VisualDebugManager } from '../development/VisualDebugManager';
import { setupSceneDebug } from '../development/setupSceneDebug';
import { createNavigationAwareObject } from '../object/createNavigationAwareObject';
import { CloudVolume } from './cloud-volume/CloudVolume';
import { Flame } from '../object/primitives/Flame';
import type { TileParseWorkerClient } from '../workers/TileParseWorkerClient';
import type { WorkerLike } from './terrain/TileMeshClient';
import type { CompositeInstance, CompositeDef, CompositeState } from '../composite';
import type { SoundEmitterRuntime } from '../audio/SoundEmitterRuntime';
import '../composite';

export class Scene {
  public boundingBox!: BoundingBox;
  public tickEventManager: TickEventManager;
  public features = new Map<string, SceneFeature>();
  public decals: Decal[] = [];
  public lightManager: LightManager;
  public textManager: TextManager;
  public overlayManager: OverlayManager;
  public objectManager: ObjectManager;
  public pickingManager: PickingManager;
  public textureCache: TextureCache;
  public objectFactory: RenderObjectFactory;

  public debugManager: VisualDebugManager;
  public navigation!: NavigationSystem;
  /**
   * Tiled spatial structure for physics-facing queries (floor/ceiling/raycast).
   * Concrete implementation is `TiledBvh` (triangle BVH over per-tile TLAS +
   * per-template BLAS); the public contract is the `SpatialQueryGrid` interface
   * in `engine/spatial/`.
   */
  public spatialGrid: TiledBvh | null = null;
  /**
   * Dynamic-agent registry — body-blocking capsules for player + NPCs. Used
   * by `PlayerControlBehaviour` (and NPC behaviours) to clamp movement
   * against other agents. Independent of `physicsFeature.boneHitDetection`,
   * which is bone-capsule projectile collision.
   */
  public readonly agentRegistry = new AgentRegistry();
  public terrainStreamer: TiledTerrainStreamer | null = null;
  public navMeshStreamer: TiledNavMeshStreamer | null = null;
  public objectStreamer: TiledObjectStreamer | null = null;

  /** Optional worker for off-thread navmesh + volgrid tile parsing. Set by the app before loadManifest(). */
  public tileParseWorker: TileParseWorkerClient | null = null;
  /** Optional worker for off-thread terrain tile mesh generation. Set by the app before loadManifest(). */
  public terrainGenWorker: WorkerLike | null = null;
  public instanceManager = new InstanceManager();
  /**
   * Authored polygon-prism cloud volumes. Empty when the scene has no
   * volumetric clouds — `CloudVolumePass` early-outs and skips the entire
   * pass cost. Populated from manifest at scene load and live-mutated by the
   * editor's preview controller during authoring.
   */
  public cloudVolumes: CloudVolume[] = [];

  /**
   * Procedural flame primitives (candles, campfires, torches). Drawn each
   * frame by `FlamesPass`. Empty when no flames are placed — the pass early-
   * outs and the per-frame cost goes to zero.
   */
  public flames: Flame[] = [];

  /**
   * Resolved composite definitions for the scene, keyed by compositeId.
   * Populated by SceneManifestLoader once per scene; tile-streamed composite
   * instances look up their template + slot config here.
   */
  public compositeDefs = new Map<string, CompositeDef>();
  /** Live composite instances keyed by `instanceId`. */
  public compositeInstances = new Map<string, CompositeInstance>();
  /**
   * Persistent per-instance state overrides — runtime callers of
   * `composite.setState(...)` write here so the value survives a tile
   * evict+reload (without this, walking far enough that the tile evicts
   * silently resets the instance to its placement defaults).
   */
  public compositeStateOverrides = new Map<string, Partial<CompositeState>>();

  /**
   * Live scene sound emitters, keyed by emitter id. Manifest globals install
   * once at scene load; tile-streamed emitters install/evict with their host
   * tile. `Scene.tick` advances spawn-mode timers (bed sources are autonomous
   * once started). Cleared on scene detach.
   */
  public soundEmitters = new Map<string, SoundEmitterRuntime>();

  /**
   * Audio-clip ids acquired for manifest-level emitters. Tile-streamed
   * emitters track their own acquire/release inside `TiledObjectStreamer`;
   * manifest globals persist for the scene's lifetime so we release them all
   * on scene detach. Append-only during load; cleared during `detached()`
   * after releasing each entry.
   */
  public manifestSoundEmitterClipIds: string[] = [];

  public modelRegistry = new Map<string, ParsedModel>();
  /**
   * Per-model LOD config (decimated tier meshes + lazy billboard mesh/material)
   * keyed by model name. Populated by [LodRegistrar] at scene load and reused
   * by [TiledObjectStreamer] every time a fresh InstanceGroup appears for a
   * tile-bound object — so streamed groups pick up the same LOD setup as
   * scene-resident ones without per-group GPU resource allocation.
   */
  public modelLodKits = new Map<string, ModelLodKit>();

  private readonly renderer: CompositeSceneRenderer;

  constructor(
    private engine: Engine,
    public inputHandler: InputHandler,
  ) {
    this.textureCache = new TextureCache();
    this.tickEventManager = new TickEventManager();
    this.lightManager = new LightManager(vec3.fromValues(0.2, 0.2, 0.2));
    this.textManager = engine.contextBuilder.buildTextManager(engine);
    this.overlayManager = new OverlayManager();
    this.objectManager = new ObjectManager();
    this.pickingManager = new PickingManager();

    this.renderer = new CompositeSceneRenderer();

    this.objectFactory = new RenderObjectFactory(this);

    this.debugManager = new VisualDebugManager(this.objectManager);
    setupSceneDebug(this, this.renderer);
    this.setupTickEvents();
    this.setupTextManager();
    this.setupObjectManager();
  }

  public async loadManifest(
    url: string,
    tracker?: SceneLoadProgressTracker,
  ): Promise<{ manifest: SceneManifest; namedObjects: Map<string, RenderObject[]> }> {
    const loader = new SceneManifestLoader(this);
    return loader.load(url, tracker);
  }

  public get ctx(): Context {
    return this.engine.ctx;
  }

  /** Shortcut into the engine's `AudioEngine`. Lazy under the hood; first access constructs it. */
  public get audio() {
    return this.engine.audio;
  }

  public createRenderer(): SceneRenderer {
    return this.renderer;
  }

  private setupObjectManager() {
    this.objectManager.removed.subscribe((drawable) => {
      this.instanceManager.unregisterObject(drawable);
    });
  }

  private setupTextManager() {
    this.textManager.addedText.subscribe((e) => this.objectManager.addObject(e));
    this.textManager.removedText.subscribe((e) => this.objectManager.removeObject(e));
  }

  private lastTickTime = 0;

  // CPU time of the last tick(), broken down by handler. Engine reads these
  // to populate RenderStats.tickBreakdown for the perf HUD. Sum is a strict
  // subset of sceneTickMs — the remainder (handlers registered outside this
  // class, e.g. PlayerController, NavigationAwareObject, app stress lights)
  // shows up as `sceneOther` in the breakdown.
  public readonly lastTickBreakdown = {
    lightAnimMs: 0,
    objAnimMs: 0,
    featuresMs: 0,
    decalsMs: 0,
    physicsMs: 0,
    terrainStreamMs: 0,
    objectStreamMs: 0,
    // Counters drained from TiledTerrainStreamer each tick. These cover work
    // that runs outside `terrainStreamer.updateCamera/tick` — specifically the
    // post-await continuation in `loadTile` (GltfMesh + SurfaceIndexMap GL
    // creation + rasterizeTile) and per-tile teardown — and so live alongside
    // `terrainStreamMs` rather than as a subset of it.
    terrainStreamInstalls: 0,
    terrainStreamInstallMs: 0,
    terrainStreamMaxInstallMs: 0,
    terrainStreamEvicts: 0,
    terrainStreamEvictMs: 0,
    terrainStreamMaxEvictMs: 0,
  };

  private setupTickEvents() {
    this.tickEventManager.registerFrameEvent(() => {
      const t = performance.now();
      for (let light of this.lightManager.lights) {
        if (light.animatedProperties) {
          light.animatedProperties.step();
        }
      }
      this.lastTickBreakdown.lightAnimMs = performance.now() - t;
    });

    this.tickEventManager.registerFrameEvent(() => {
      const t = performance.now();
      for (const obj of this.objectManager.getAllObjects()) {
        if (obj.renderConfiguration.modes?.includes(RenderMode.Primary) && obj.animatedProperties) {
          obj.animatedProperties.step();
        }
      }
      this.lastTickBreakdown.objAnimMs = performance.now() - t;
    });

    // Feature tick with deltaTime
    this.tickEventManager.registerFrameEvent(() => {
      const now = performance.now();
      const dt = this.lastTickTime > 0 ? (now - this.lastTickTime) / 1000 : 0;
      this.lastTickTime = now;

      let t = performance.now();
      for (const feature of this.features.values()) {
        feature.tick(dt);
      }
      this.lastTickBreakdown.featuresMs = performance.now() - t;

      t = performance.now();
      for (let i = this.decals.length - 1; i >= 0; i--) {
        if (!this.decals[i].tick(dt)) {
          this.decals.splice(i, 1);
        }
      }
      this.lastTickBreakdown.decalsMs = performance.now() - t;

      t = performance.now();
      if (this.spatialGrid) {
        const pos = this.ctx.engine.activeCamera.cameraPosition;
        this.spatialGrid.updateCamera(pos[0], pos[2]);
      }
      this.lastTickBreakdown.physicsMs = performance.now() - t;

      // Composite tick — flicker behaviours mutate light.color in place.
      // Not bucketed separately in tickBreakdown: cost is sub-µs per light
      // and composite counts are small (lanterns, torches — tens), so this
      // falls into `sceneOther` rather than adding a perf-overlay row.
      if (this.compositeInstances.size > 0) {
        const dtMs = dt * 1000;
        for (const c of this.compositeInstances.values()) c.tick(dtMs);
      }

      // Sound emitter tick — spawn-mode timers + bed-source retry. Bed mode
      // ticks are a no-op once the source is live, so the cost stays sub-µs
      // per emitter even at hundreds placed.
      if (this.soundEmitters.size > 0) {
        for (const e of this.soundEmitters.values()) e.tick();
      }

      t = performance.now();
      if (this.terrainStreamer) {
        const pos = this.ctx.engine.activeCamera.cameraPosition;
        this.terrainStreamer.updateCamera(pos[0], pos[2]);
        this.terrainStreamer.tick(pos[0], pos[2]);
      }
      this.lastTickBreakdown.terrainStreamMs = performance.now() - t;

      // Drain streaming counters (work that ran outside the timed window
      // above — async `loadTile` continuations resolved between/within
      // frames). See lastTickBreakdown for context.
      if (this.terrainStreamer) {
        const s = this.terrainStreamer.readAndResetStreamStats();
        this.lastTickBreakdown.terrainStreamInstalls = s.installs;
        this.lastTickBreakdown.terrainStreamInstallMs = s.installCpuMs;
        this.lastTickBreakdown.terrainStreamMaxInstallMs = s.maxInstallCpuMs;
        this.lastTickBreakdown.terrainStreamEvicts = s.evicts;
        this.lastTickBreakdown.terrainStreamEvictMs = s.evictCpuMs;
        this.lastTickBreakdown.terrainStreamMaxEvictMs = s.maxEvictCpuMs;
      } else {
        this.lastTickBreakdown.terrainStreamInstalls = 0;
        this.lastTickBreakdown.terrainStreamInstallMs = 0;
        this.lastTickBreakdown.terrainStreamMaxInstallMs = 0;
        this.lastTickBreakdown.terrainStreamEvicts = 0;
        this.lastTickBreakdown.terrainStreamEvictMs = 0;
        this.lastTickBreakdown.terrainStreamMaxEvictMs = 0;
      }

      t = performance.now();
      if (this.objectStreamer) {
        const pos = this.ctx.engine.activeCamera.cameraPosition;
        this.objectStreamer.updateCamera(pos[0], pos[2]);
      }
      this.lastTickBreakdown.objectStreamMs = performance.now() - t;
    });
  }

  public createNavigationAwareObject(
    obj: { position: vec3 | number[] },
    speed: number,
  ): NavigationAwareObject {
    // If `obj` is a RenderObject, ensure it has an agent registered (idempotent
    // — `registerAnimated` elsewhere in the scene loader may already have run
    // and the WeakMap keeps both wiring paths on a single Agent instance).
    let selfAgent = null;
    const asRender = obj as Partial<RenderObject>;
    if (asRender.bounds && asRender.trs) {
      selfAgent = this.agentRegistry.ensureForObject(obj as RenderObject);
    }
    return createNavigationAwareObject(
      {
        navigation: this.navigation,
        spatialGrid: this.spatialGrid,
        tickEventManager: this.tickEventManager,
        physics: this.features.get('physics') as PhysicsFeature | undefined,
        agentRegistry: this.agentRegistry,
        selfAgent,
      },
      obj,
      speed,
    );
  }

  public attached() {}

  public detached() {
    const gl = this.ctx.gl;

    // Destroy features
    for (const feature of this.features.values()) {
      feature.destroy?.();
    }
    this.features.clear();

    // Destroy textures
    this.textureCache.destroy(gl);

    // Destroy shadow maps on lights
    this.lightManager.destroy(gl);

    // Destroy unique meshes owned by render objects (VAOs, vertex/index
    // buffers, animation data textures). Meshes can be shared across
    // RenderObjects (instancing, model registry), so Mesh.destroy() is
    // idempotent — calling it more than once is safe.
    for (const obj of this.objectManager.getAllObjects()) {
      // TODO: clean this yucky cast
      const mesh = (obj as RenderObject).mesh;
      if (mesh) mesh.destroy(gl);
    }

    // Clear objects and text (calls destroy() on each DrawableObject so
    // visualizers and other drawables can release GL resources too)
    this.objectManager.clear();
    this.textManager.destroyAll();
    this.overlayManager.clear();

    // Drop the model registry — meshes inside have already been destroyed above
    this.modelRegistry.clear();
    // LOD kits hold their own decimated GltfMesh + lazy billboard mesh/texture;
    // ObjectManager.clear() destroyed any meshes that were attached to live
    // RenderObjects, but the LOD-only meshes never were so they leak unless
    // we walk the kits and destroy them here.
    for (const kit of this.modelLodKits.values()) {
      for (const tier of kit.decimatedTiers) tier.mesh.destroy(gl);
      kit.billboardMesh?.destroy(gl);
    }
    this.modelLodKits.clear();

    // Tear down composite instances. Children (mesh + light) are owned by
    // their respective managers and will be destroyed in the normal teardown
    // above; `destroy()` here is the canonical place to release any composite-
    // specific resources (currently a no-op besides removing the mesh / light
    // from the managers, which we already do — but keeping this call site
    // means future composites with extra resources can hook in cleanly).
    for (const c of this.compositeInstances.values()) c.destroy(this);
    this.compositeInstances.clear();
    this.compositeDefs.clear();
    this.compositeStateOverrides.clear();

    // Tear down sound emitters (manifest-globals + any tile-streamed leftovers
    // that didn't clean themselves up via tile evict). Release manifest-level
    // clip acquires symmetrically with `SceneManifestLoader`. Guard so a scene
    // that never had a manifest emitter doesn't force lazy `engine.audio`
    // construction (CLI/headless callers must stay cheap).
    for (const e of this.soundEmitters.values()) e.destroy();
    this.soundEmitters.clear();
    if (this.manifestSoundEmitterClipIds.length > 0) {
      const lib = this.ctx.engine.audio.library;
      for (const clipId of this.manifestSoundEmitterClipIds) lib.release(clipId);
      this.manifestSoundEmitterClipIds = [];
    }

    // Clear ephemeral per-scene state
    this.decals = [];
    this.cloudVolumes = [];
    this.flames = [];
    this.terrainStreamer = null;
    this.navMeshStreamer = null;
    if (this.objectStreamer) {
      this.objectStreamer.destroy();
      this.objectStreamer = null;
    }
    // Reset GL state caches so stale shader/texture references don't interfere with the next scene
    this.ctx.resetState();

    // Drop temporal accumulation state — history textures from this scene must
    // not leak ghost frames into the next one.
    this.ctx.engine.activeCamera?.invalidateHistory();
  }
}
